/* eslint-disable radix */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import reactCSS from 'reactcss';
import map from 'lodash/map';
import merge from 'lodash/merge';
import * as material from 'material-colors';
import { CustomPicker } from 'react-color';
import { isValidHex } from 'react-color/lib/helpers';
import SwatchesGroup from 'react-color/lib/components/swatches/SwatchesGroup';
import { rgbToHex, hexToRgb } from '@material-ui/core/styles/colorManipulator';
import CompactFields from './CompactFields';

const rgbObjectToString = rgbObject => {
	console.log('rgbObjectToString', rgbObject);
	return `rgb(${rgbObject?.r || 0},${rgbObject?.g || 0},${rgbObject?.b || 0})`;
};
const rgbStringToObject = rgbString => {
	if (rgbString) {
		const rgbArray = rgbString
			.replace('rgb(', '')
			.replace(')', '')
			.split(',');
		return {
			// eslint-disable radix
			r: parseInt(rgbArray[0], 0),
			g: parseInt(rgbArray[1], 0),
			b: parseInt(rgbArray[2], 0)
		};
	}

	return null;
};
const ColorPicker = ({
	width,
	height,
	onChange,
	onSwatchHover,
	colors,
	defaultHex,
	styles: passedStyles = {},
	className = ''
}) => {
	const [selectedColor, setSelectedColor] = useState(defaultHex || '#000000');
	const [selectedRGB, setSelectedRGB] = useState(
		rgbStringToObject(hexToRgb(defaultHex || '#000000'))
	);
	const styles = reactCSS(
		merge(
			{
				default: {
					picker: {
						width,
						height
					},
					overflow: {
						// height: height - 30,
						overflowY: 'scroll'
					},
					body: {
						padding: '16px 0 6px 16px'
					},
					clear: {
						clear: 'both'
					},
					container: {
						height: '100%',
						display: 'flex',
						flexDirection: 'column'
					},
					codeContainer: {
						padding: '16px 0 4px 16px'
					}
				}
			},
			passedStyles
		)
	);
	const handleChange = (data, e) => {
		console.log(data, e);
		if (data?.source === 'hex' && !isValidHex(data?.hex)) return;

		let hexData = data;
		if (data?.source === 'rgb') {
			const hex = rgbToHex(rgbObjectToString(data));
			console.log('handle rgb change', data, rgbObjectToString(data?.rgb), hex);
			hexData = { hex, source: 'hex' };
			setSelectedRGB(data);
			setSelectedColor(hexData.hex);
		} else if (data?.source === 'hex') {
			console.log('handle change', data);
			setSelectedColor(data?.hex);
			setSelectedRGB(rgbStringToObject(hexToRgb(data?.hex)));
		} else {
			console.log('handle change', data);
			hexData = { hex: data, source: 'hex' };
			setSelectedColor(data);
			setSelectedRGB(rgbStringToObject(hexToRgb(data)));
		}

		onChange(hexData, e);
	};

	return (
		<div style={styles.picker} className={className}>
			<div style={styles.container}>
				<div style={styles.overflow}>
					<div style={styles.body}>
						{map(colors, group => (
							<SwatchesGroup
								key={group.toString()}
								group={group}
								active={selectedColor}
								onClick={handleChange}
								onSwatchHover={onSwatchHover}
							/>
						))}
						<div style={styles.clear} />
					</div>
				</div>
				<div style={styles.codeContainer}>
					<CompactFields hex={selectedColor} rgb={selectedRGB} onChange={handleChange} />
				</div>
			</div>
		</div>
	);
};

ColorPicker.propTypes = {
	width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	colors: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
	styles: PropTypes.object,
	onChange: PropTypes.func,
	onSwatchHover: PropTypes.func,
	className: PropTypes.string,
	defaultHex: PropTypes.string
};

/* eslint-disable max-len */
ColorPicker.defaultProps = {
	width: 520,
	height: 340,
	defaultHex: '#000',
	colors: [
		[
			material.red['900'],
			material.red['700'],
			material.red['500'],
			material.red['300'],
			material.red['100']
		],
		[
			material.pink['900'],
			material.pink['700'],
			material.pink['500'],
			material.pink['300'],
			material.pink['100']
		],
		[
			material.purple['900'],
			material.purple['700'],
			material.purple['500'],
			material.purple['300'],
			material.purple['100']
		],
		[
			material.deepPurple['900'],
			material.deepPurple['700'],
			material.deepPurple['500'],
			material.deepPurple['300'],
			material.deepPurple['100']
		],
		[
			material.indigo['900'],
			material.indigo['700'],
			material.indigo['500'],
			material.indigo['300'],
			material.indigo['100']
		],
		[
			material.blue['900'],
			material.blue['700'],
			material.blue['500'],
			material.blue['300'],
			material.blue['100']
		],
		[
			material.lightBlue['900'],
			material.lightBlue['700'],
			material.lightBlue['500'],
			material.lightBlue['300'],
			material.lightBlue['100']
		],
		[
			material.cyan['900'],
			material.cyan['700'],
			material.cyan['500'],
			material.cyan['300'],
			material.cyan['100']
		],
		[
			material.teal['900'],
			material.teal['700'],
			material.teal['500'],
			material.teal['300'],
			material.teal['100']
		],
		[
			'#194D33',
			material.green['700'],
			material.green['500'],
			material.green['300'],
			material.green['100']
		],
		[
			material.lightGreen['900'],
			material.lightGreen['700'],
			material.lightGreen['500'],
			material.lightGreen['300'],
			material.lightGreen['100']
		],
		[
			material.lime['900'],
			material.lime['700'],
			material.lime['500'],
			material.lime['300'],
			material.lime['100']
		],
		[
			material.yellow['900'],
			material.yellow['700'],
			material.yellow['500'],
			material.yellow['300'],
			material.yellow['100']
		],
		[
			material.amber['900'],
			material.amber['700'],
			material.amber['500'],
			material.amber['300'],
			material.amber['100']
		],
		[
			material.orange['900'],
			material.orange['700'],
			material.orange['500'],
			material.orange['300'],
			material.orange['100']
		],
		[
			material.deepOrange['900'],
			material.deepOrange['700'],
			material.deepOrange['500'],
			material.deepOrange['300'],
			material.deepOrange['100']
		],
		[
			material.brown['900'],
			material.brown['700'],
			material.brown['500'],
			material.brown['300'],
			material.brown['100']
		],
		[
			material.blueGrey['900'],
			material.blueGrey['700'],
			material.blueGrey['500'],
			material.blueGrey['300'],
			material.blueGrey['100']
		],
		['#000000', '#525252', '#969696', '#D9D9D9', '#FFFFFF']
	],
	styles: {}
};

export default CustomPicker(ColorPicker);
